import React from 'react';
import { getDataset } from 'app/data/datasets';
import { Panel } from './ModernPlanBox';

/**
 * The sample previously showed `sov.data('query')` four times — the literal
 * placeholder string, never substituted. It now uses a real dataset from the
 * registry.
 *
 * `sdkQuery` values are now SDK-verified — they were pulled from the sovai
 * package and executed, not inferred from docs URLs as they originally were.
 * The snippet still links to the dataset's documentation as the authoritative
 * reference. No example output is shown, because we have not captured any.
 */
const EXAMPLE_SLUG = 'bankruptcy-predictions';

/**
 * Comment colour: was `#565869`, which measured 2.44:1 against the code ground
 * and failed WCAG AA for normal text. `#9498ad` measures 6.63:1 on the `#0d1117`
 * ground this panel now sits on.
 */
const COMMENT = 'text-[#9498ad]';

const CodeBlock = () => {
  const example = getDataset(EXAMPLE_SLUG);
  const query = example?.sdkQuery ?? '';

  const Call = ({ method, comment }: { method: string; comment: string }) => (
    <>
      <div className={COMMENT}># {comment}</div>
      <div className="overflow-x-auto whitespace-nowrap">
        <span className="text-[#bd93f9]">sov</span>
        <span className="text-gray-300">.{method}(</span>
        <span className="text-[#50fa7b]">'{query}'</span>
        <span className="text-gray-300">)</span>
      </div>
    </>
  );

  return (
    <div className="w-full md:w-[500px] lg:w-[600px] xl:w-[700px]">
      {/* Notched panel label replaces the imitation macOS title bar. */}
      <Panel label="python" className="bg-gray-900/40">
        <div className="space-y-3 px-4 pb-4 pt-6 font-mono text-sm md:text-base">
          <div className={COMMENT}># pip install sovai</div>
          <div>
            <span className="text-[#ff79c6]">import</span>{' '}
            <span className="text-[#bd93f9]">sovai</span>{' '}
            <span className="text-[#ff79c6]">as</span>{' '}
            <span className="text-[#bd93f9]">sov</span>
          </div>

          <Call
            method="data"
            comment={`${example?.name ?? 'Dataset'} — history from ${
              example?.historyStart ?? 'see docs'
            }`}
          />
          <Call method="plots" comment="Chart the same dataset" />
          <Call method="reports" comment="Generate a report over it" />
          <Call method="compute" comment="Run the packaged computations" />
        </div>

        <p className="border-t border-gray-800 px-4 py-2.5 text-[11px] leading-relaxed text-gray-400">
          Query strings are SDK-verified. Full parameters and return schema are
          in{' '}
          <a
            href={example?.docsUrl ?? 'https://docs.sov.ai/'}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-400 underline underline-offset-2 hover:text-blue-300 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-blue-400"
          >
            the {example?.name ?? 'dataset'} docs
          </a>
          .
        </p>
      </Panel>
    </div>
  );
};

/**
 * Steps were marked with green tick images whose alt text read "Completed Step
 * 1" — nothing is completed, so the ticks asserted state that did not exist.
 * They are now monospace ordinals, which carry the sequence without the claim.
 */
const STEPS = [
  'Choose a dataset you are interested in',
  'Extract signals from the datasets',
  'Improve with statistical software',
];

export const ReversedCard = () => {
  return (
    // The decorative purple blur orb (a JPEG background) has been removed in
    // favour of the flat ground used across the catalogue pages.
    <section className="w-full bg-[#0d1117] px-4 py-16 sm:px-6 lg:px-8">
      {/*
        Both columns align to the top. Vertically centring them left the
        heading floating mid-panel against the taller code block, so the two
        columns read as unrelated blocks rather than one row.
      */}
      <div className="mx-auto flex max-w-7xl flex-col items-start justify-center gap-10 md:flex-row md:items-start lg:gap-16">
        {/* Left column: the three steps. */}
        <div className="flex w-full flex-col items-start gap-y-6 md:w-1/2">
          <h2 className="font-mono text-3xl font-semibold text-white lg:text-4xl">
            <span className="text-blue-400">Python</span> First!
          </h2>

          <ol className="flex w-full flex-col">
            {STEPS.map((step, index) => (
              <li
                key={step}
                className="flex items-baseline gap-3 border-b border-dashed border-gray-800/70 py-2.5 last:border-b-0"
              >
                <span className="font-mono text-xs tabular-nums text-gray-400">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span className="text-base leading-relaxed text-gray-300 lg:text-lg">
                  {step}
                </span>
              </li>
            ))}
          </ol>

          {/*
            One route to the tutorials, not two. There used to be this link
            plus a floating "Free Tutorials" popup pointing at the same page,
            so a reader had to parse two competing calls and dismiss one of
            them. The popup is gone; this is the single affordance.
          */}
          <a
            href="https://docs.sov.ai/get-started/tutorials"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 border border-gray-700 bg-transparent px-3.5 py-2 font-mono text-xs text-gray-200 hover:border-gray-500 hover:text-white focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-blue-400"
          >
            See example notebooks
            <span
              aria-hidden="true"
              className="text-gray-400 group-hover:text-white"
            >
              →
            </span>
          </a>
        </div>

        {/* Right column: the code sample. */}
        <div className="flex w-full justify-center md:w-1/2">
          <CodeBlock />
        </div>
      </div>
    </section>
  );
};
